// web/lib/career/searchRelevance.ts
// PURE careers-search relevance helpers, with NO server imports, so BOTH the server search service
// (web/lib/career/search.ts, ranks rows within a type) AND the client command palette
// (web/components/max/GlobalSearch.tsx, ranks every type into one relevance-ordered list) share one
// definition. Keep this file free of any server-only import (e.g. getServiceRoleClient) - it is
// bundled into the browser.

/** Minimum query length before we hit the DB (typeahead noise floor). */
export const MIN_QUERY_LEN = 2

// Filler words that describe "an internship search" rather than the ROLE. Dropping them turns
// "AI Science Intern" -> ["ai","science"] so the title match is about the work, not the word
// "intern" (every posting is an internship, so it carries no signal). Seasons/terms/years are
// handled by the feed's structured filter parse, not by title text.
const SEARCH_STOPWORDS = new Set([
  "intern", "interns", "internship", "internships", "co-op", "coop", "co", "op",
  "job", "jobs", "role", "roles", "position", "positions", "opening", "openings", "program", "programs",
  "summer", "fall", "autumn", "spring", "winter",
  "the", "a", "an", "and", "or", "for", "of", "in", "at", "to", "with", "on",
  "2024", "2025", "2026", "2027", "2028", "2029", "2030",
])

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")

/**
 * The meaningful search tokens: lowercase, alphanumeric, >= 2 chars, filler words dropped, deduped.
 * Empty when the query is only filler (e.g. "internships") - callers then fall back to the raw query.
 * Pure + unit-tested.
 */
export function queryTokens(q: string): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  for (const raw of (q || "").toLowerCase().split(/[^a-z0-9]+/)) {
    if (raw.length < 2) continue
    if (SEARCH_STOPWORDS.has(raw)) continue
    if (seen.has(raw)) continue
    seen.add(raw)
    out.push(raw)
  }
  return out
}

/**
 * Relevance of a candidate's text to the query, 0..100. Deterministic and shared by BOTH the
 * server (rank rows within a type before truncating) and the palette client (rank ALL types into
 * one list). Exact > prefix > word-boundary phrase > substring > token coverage. Pure + tested.
 */
export function relevanceScore(text: string | null | undefined, query: string): number {
  const t = String(text || "").toLowerCase().trim()
  const ql = String(query || "").toLowerCase().trim()
  if (!t || !ql) return 0
  if (t === ql) return 100
  if (t.startsWith(ql)) return 88
  if (new RegExp(`\\b${escapeRegExp(ql)}`).test(t)) return 74 // query begins a word in the text
  if (t.includes(ql)) return 62 // contiguous substring anywhere
  const toks = queryTokens(ql)
  if (toks.length) {
    const hits = toks.filter((w) => t.includes(w))
    if (hits.length) return Math.round(30 + (hits.length / toks.length) * 28) // 30..58 by coverage
  }
  return 0
}
